import { useState, useEffect } from "react";
import Card from "@/components/Card";
import { Building2, Users, MapPin, Calendar, Loader2 } from "lucide-react";
import apiClient from "@/lib/api-client";

interface GlobalStats {
  overview: {
    totalOrganizations: number;
    activeOrganizations: number;
    totalUsers: number;
    totalLocations: number;
    totalCustomers: number;
    totalReservations: number;
  };
  organizationsByPlan: Array<{
    planName: string;
    count: number;
  }>;
  recentOrganizations: Array<{
    id: string;
    name: string;
    createdAt: string;
    subscriptionStatus: string;
    plan: { name: string };
  }>;
}

export default function Analytics() {
  const [stats, setStats] = useState<GlobalStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.get("/admin/stats");
      setStats(response.data.data);
    } catch (err) {
      setError("Error al cargar estadísticas");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      active: "bg-green-100 text-green-800",
      trialing: "bg-blue-100 text-blue-800",
      suspended: "bg-red-100 text-red-800",
      cancelled: "bg-gray-100 text-gray-800",
    };
    const labels: Record<string, string> = {
      active: "Activo",
      trialing: "Prueba",
      suspended: "Suspendido",
      cancelled: "Cancelado",
    };
    return (
      <span
        className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status] || styles.active}`}
      >
        {labels[status] || status}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
        {error}
      </div>
    );
  }

  const metrics = [
    {
      name: "Organizaciones",
      value: stats?.overview.totalOrganizations || 0,
      subtext: `${stats?.overview.activeOrganizations || 0} activas`,
      icon: Building2,
      color: "bg-blue-50 text-blue-600",
    },
    {
      name: "Usuarios",
      value: stats?.overview.totalUsers || 0,
      subtext: "En toda la plataforma",
      icon: Users,
      color: "bg-green-50 text-green-600",
    },
    {
      name: "Restaurantes",
      value: stats?.overview.totalLocations || 0,
      subtext: "Ubicaciones registradas",
      icon: MapPin,
      color: "bg-purple-50 text-purple-600",
    },
    {
      name: "Reservas",
      value: stats?.overview.totalReservations || 0,
      subtext: `${stats?.overview.totalCustomers || 0} clientes`,
      icon: Calendar,
      color: "bg-orange-50 text-orange-600",
    },
  ];

  return (
    <div className="space-y-4 md:space-y-6">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-gray-900">
          Estadísticas Globales
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          Vista general de toda la plataforma ReservaManager
        </p>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metrics.map((metric) => (
          <Card key={metric.name}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  {metric.name}
                </p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {metric.value.toLocaleString("es-ES")}
                </p>
                <p className="text-sm text-gray-500 mt-1">{metric.subtext}</p>
              </div>
              <div className={`p-3 rounded-lg ${metric.color}`}>
                <metric.icon size={24} />
              </div>
            </div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Organizations by Plan */}
        <Card title="Organizaciones por Plan">
          <div className="space-y-4">
            {stats?.organizationsByPlan.map((item) => (
              <div
                key={item.planName}
                className="flex items-center justify-between"
              >
                <span className="text-gray-700">{item.planName}</span>
                <div className="flex items-center space-x-3">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-primary-600 h-2 rounded-full"
                      style={{
                        width: `${Math.min(100, (item.count / (stats?.overview.totalOrganizations || 1)) * 100)}%`,
                      }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-900 w-8 text-right">
                    {item.count}
                  </span>
                </div>
              </div>
            ))}
            {(!stats?.organizationsByPlan ||
              stats.organizationsByPlan.length === 0) && (
              <p className="text-gray-500 text-center py-4">
                No hay datos disponibles
              </p>
            )}
          </div>
        </Card>

        {/* Recent Organizations */}
        <Card title="Organizaciones Recientes">
          <div className="space-y-4">
            {stats?.recentOrganizations.map((org) => (
              <div
                key={org.id}
                className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
              >
                <div>
                  <p className="font-medium text-gray-900">{org.name}</p>
                  <p className="text-sm text-gray-500">
                    {org.plan.name} • {formatDate(org.createdAt)}
                  </p>
                </div>
                {getStatusBadge(org.subscriptionStatus)}
              </div>
            ))}
            {(!stats?.recentOrganizations ||
              stats.recentOrganizations.length === 0) && (
              <p className="text-gray-500 text-center py-4">
                No hay organizaciones recientes
              </p>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
